import React, { useCallback, useEffect, useState } from "react";
import ReactSelect from "react-select";

const Select = ({
  options,
  className,
  placeholder,
  onChange,
  value,
  label,
  labelClassName,
  required,
  isDisabled,
  isSearchable,
  isMulti,
  loading,
  closeMenuOnSelect,
  errorMsg,
  customStyles,
}) => {
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") === "dark" ? "dark" : "light"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = localStorage.getItem("theme") === "dark";
      setTheme(isDark ? "dark" : "light");
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const getCustomStyles = useCallback((theme = "light") => {
    const isDark = theme === "dark";

    return {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
        borderWidth: "1px",
        borderColor: isDark ? "#4B5563" : "#D1D5DB",
        borderRadius: "0.375rem",
        opacity: state.isDisabled ? 0.5 : 1,
        color: isDark ? "#F9FAFB" : "#111827",
        boxShadow: state.isFocused
          ? `0 0 0 1px ${isDark ? "#3B82F6" : "#2563EB"}`
          : "none",
      }),
      valueContainer: (provided) => ({
        ...provided,
        paddingLeft: "1rem",
        fontSize: "14px",
        color: isDark ? "#F9FAFB" : "#111827",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        paddingRight: "0.5rem",
        color: isDark ? "#F9FAFB" : "#111827",
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDark ? "#9CA3AF" : "#374151",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDark ? "#F9FAFB" : "#111827",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
        color: isDark ? "#F9FAFB" : "#111827",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused
          ? isDark
            ? "#374151"
            : "#E5E7EB"
          : "transparent",
        color: isDark ? "#F9FAFB" : "#111827",
        cursor: "pointer",
      }),
    };
  }, []);

  const getValue = (isMulti, options, value) => {
    if (isMulti) {
      return options?.filter((o) => value?.includes(o?.value));
    }
    return options?.find((o) => o?.value === value);
  };

  return (
    <div className={className}>
      {label && (
        <label
          // htmlFor={name}
          className={
            labelClassName ||
            "text-sm mb-1 font-semibold text-gray-900 dark:text-gray-200"
          }
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <ReactSelect
        className="relative"
        styles={customStyles ? customStyles : getCustomStyles(theme)}
        options={options}
        isSearchable={isSearchable}
        placeholder={placeholder ?? ""}
        onChange={onChange}
        value={getValue(isMulti, options, value) || ""}
        isDisabled={isDisabled}
        isMulti={isMulti}
        isLoading={loading}
        closeMenuOnSelect={closeMenuOnSelect}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "#2C59F8",
            primary75: "#fb923c",
            primary50: "#cbd5e1",
            primary25: "#e2e8f0",
            neutral0: "#f7f7fa",
            neutral5: "",
            neutral10: "",
            neutral20: "",
            neutral30: "",
            neutral40: "",
            neutral50: "",
            neutral60: "",
            neutral70: "",
            neutral80: "",
            neutral90: "",
          },
        })}
      />
      {errorMsg && (
        <span className="mt-1 text-xs font-semibold text-error">
          {errorMsg}
        </span>
      )}
    </div>
  );
};

export default Select;
